import { z } from 'zod';
import { ProductCondition, ProductStatus } from './status.ts';
import { ProductImage, ProductImageInput } from './image.ts';
import { PriceUnit } from './parts.ts';

// General merchandise the shop sells that isn't a vehicle (Inventory) or a
// catalogued replacement part (Parts) — e.g. used tires, wheels, accessories.
// Deliberately lighter than Parts: no unique part number, description optional,
// but adds a new/used condition.

const money = z.number().nonnegative().nullable().optional();

export const ProductBase = z.object({
  sku: z.string().max(100).nullable().optional(),
  name: z.string().min(1).max(200),
  condition: ProductCondition.default('used'),

  regular_price: money,
  sale_price: money,
  price_unit: PriceUnit.nullable().optional(),

  brand_id: z.number().int().positive().nullable(),
  category_id: z.number().int().positive().nullable(),

  description: z.string().max(20_000).nullable().optional(),
});

export const ProductCreateInput = ProductBase.extend({
  status: ProductStatus.default('draft'),
  images: z.array(ProductImageInput).default([]),
});
export type ProductCreateInput = z.infer<typeof ProductCreateInput>;

export const ProductUpdateInput = ProductBase.partial().extend({
  status: ProductStatus.optional(),
});
export type ProductUpdateInput = z.infer<typeof ProductUpdateInput>;

export const Product = ProductBase.extend({
  id: z.number().int().positive(),
  slug: z.string(),
  status: ProductStatus,
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  published_at: z.string().datetime().nullable(),
  brand_name: z.string().nullable(),
  category_name: z.string().nullable(),
  images: z.array(ProductImage),
});
export type Product = z.infer<typeof Product>;

export const ProductBulkActionInput = z.object({
  action: z.enum(['publish', 'hide', 'mark_out_of_stock', 'delete']),
  ids: z.array(z.number().int().positive()).min(1).max(500),
});
export type ProductBulkActionInput = z.infer<typeof ProductBulkActionInput>;

export const ProductListQuery = z.object({
  q: z.string().trim().optional(),
  status: z.union([ProductStatus, z.array(ProductStatus)]).optional(),
  condition: ProductCondition.optional(),
  brand_id: z.coerce.number().int().positive().optional(),
  category_id: z.coerce.number().int().positive().optional(),
  min_price: z.coerce.number().nonnegative().optional(),
  max_price: z.coerce.number().nonnegative().optional(),
  sort: z
    .enum(['newest', 'oldest', 'price_asc', 'price_desc', 'name_asc', 'name_desc'])
    .default('newest'),
  page: z.coerce.number().int().min(1).default(1),
  page_size: z.coerce.number().int().min(1).max(100).default(20),
});
export type ProductListQuery = z.infer<typeof ProductListQuery>;
